import React, { useEffect, useState, useRef } from "react";
import { Organization, ProviderType, NewIntegration, Member } from "../common/ObjectTypes";
import SchemaForm from "../common/SchemaForm";
import SelectInput from "../common/SelectInput";
import { ArrowDown, ArrowRight, AttentionIcon } from "../common/ProjectIcons";
import { useTranslation } from "react-i18next";
import { checkSchema } from "../common/SchemaFormTemplates/checkSchema";
import { getButtonText } from "../common/TestActionFunctions";
import { Tooltip } from "../common/Tooltip";
import ReactGA4 from "react-ga4";
import { getEnvironment } from "../common/getEnvironment";
import { inputClasses } from "../common/tailwindClassnames";
import { useHelp, HelpCard } from "../../context/HelpContext";

type NewConnectionStepTwoProps = {
    currentMemberIsAdmin: boolean,
    initialConnection: NewIntegration,
    errors: any,
    rsjfErrors: any,
    selectedProviderType: ProviderType,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onChangeEndpoint: Function,
    onChangeSchemaForm: Function,
    onChangeName: Function,
    onSelectOwner: Function,
}

const NewConnectionStepTwo = ({
    currentMemberIsAdmin,
    initialConnection,
    errors,
    rsjfErrors,
    selectedProviderType,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onChangeEndpoint,
    onChangeSchemaForm,
    onChangeName,
    onSelectOwner,
}: NewConnectionStepTwoProps) => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();
    const [showAdvanced, setShowAdvanced] = useState(false);
    const formInteractionStarted = useRef(false);

    useEffect(() => {
        const cards: HelpCard[] = [
            {
                title: t('Setting up a Connection in Gundi'),
                description: t('Find step-by-step instructions for connecting your data source.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections/' },
            },
            ...(selectedProviderType.help_center_url ? [{
                title: t('{{technology}} Connection Set Up', { technology: selectedProviderType.name }),
                description: t('Configuration guide specific to {{technology}} connections.', { technology: selectedProviderType.name }),
                button: { label: t('Learn More'), url: selectedProviderType.help_center_url },
            }] : []),
        ];
        setHelpContent({ cards });
    }, [selectedProviderType, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);


    const handleFormInteraction = () => {
        if (!formInteractionStarted.current) {
            const currentEnv = getEnvironment();
            ReactGA4.event('form_start', {
                form_name: 'new_connection_form',
                provider_type: selectedProviderType.name,
                environment: currentEnv
            });
            formInteractionStarted.current = true;
        }
    };

    return (
        <div className="h-full mt-3 flex flex-row bg-very-light-gray dark:bg-off-black">
            <div className="flex flex-col pb-16 w-full max-w-[735px]">
                {/* form errors */}
                {(Object.keys(errors).length > 0 || Object.keys(rsjfErrors).length > 0) &&
                    <div className="bg-very-light-orange dark:bg-orange-900 dark:bg-opacity-30 py-2 px-3 rounded-sm mb-6 text-dark-gray dark:text-white transition-colors duration-300">
                        <AttentionIcon /> {t("There are fields that require your attention. Please check and correct the errors.")}
                    </div>}
                {/* connection organization and connection name box */}
                <div className="rounded p-[16px] border border-gray-lines dark:border-neutral-gray-600 bg-white dark:bg-dark-gray mb-[16px] transition-colors duration-300">
                    <label className="text-off-black dark:text-white text-lg mt-8 transition-colors duration-300">
                        {t("Organize this connection. Consider adding a name that will help differentiate this connection from another.")}
                    </label>
                    <div className="mt-3 flex flex-row">
                        {/* organization */}
                        <div className="flex flex-col w-1/2 mr-3 mt-1 pb-1">
                            <label
                                className="inline-block hover:cursor-pointer text-sm text-secondary-gray dark:text-gray-lines transition-colors duration-300"
                            >
                                {t("Connection Workspace")} *
                            </label>
                            <SelectInput
                                options={organizations.map((org: Organization) => {
                                    return {
                                        label: org.name,
                                        value: org.id
                                    }
                                })}
                                className={"basic-single dark:text-white"}
                                autoSelectSingleOption={true}
                                isDisabled={false}
                                isLoading={false}
                                isClearable={false}
                                isSearchable={true}
                                isMulti={false}
                                name={t("Workspace")}
                                onFocus={handleFormInteraction}
                                onChange={(e: any) => onSelectOwner(e)}
                                onMenuScrollToBottom={loadMoreOrgs}
                                handleInputChange={(e: any) => orgInputChange(e)}
                            />
                            {errors.owner && <div className="text-orange-600 dark:text-orange-400 py-1 rounded flex flex-row items-center transition-colors duration-300"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.owner}</p></div>}
                        </div>
                        {/* name */}
                        <div className="flex flex-col w-1/2 mr-3 mt-1">
                            <label
                                className="inline-block hover:cursor-pointer text-sm text-secondary-gray dark:text-gray-lines transition-colors duration-300"
                            >
                                {t("Connection Name")} *
                            </label>
                            <input
                                id="name"
                                placeholder=""
                                value={initialConnection.name}
                                className={inputClasses.standard}
                                onFocus={handleFormInteraction}
                                onChange={(e) => onChangeName(e)}
                                disabled={false}
                            >
                            </input>
                            {errors.name && <div className="text-orange-600 dark:text-orange-400 py-1 rounded flex flex-row items-center transition-colors duration-300"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.name}</p></div>}
                        </div>
                    </div>
                </div>

                {/* configuration actions box */}
                {selectedProviderType.actions &&
                    selectedProviderType.actions.length > 0 &&
                    selectedProviderType.actions.some((item: any) =>
                        item.schema &&
                        Object.keys(item.schema).length > 0
                    ) && (
                        <div className="rounded p-[16px] border border-gray-lines dark:border-neutral-gray-600 bg-white dark:bg-dark-gray transition-colors duration-300">
                            <div className="flex flex-row items-center justify-between w-full mb-[16px]">
                                <label className="text-off-black dark:text-white text-lg transition-colors duration-300">{t("Enter the following information to connect to your data provider")}</label>
                                {/* Test action button aligned with title */}
                                {selectedProviderType.actions.some((item: any) => item.schema && item.schema.is_executable === true) && (
                                    <div className="flex flex-row items-center">
                                        <button disabled className="opacity-100 bg-light-gray-button dark:bg-neutral-gray-700 text-secondary-gray dark:text-neutral-gray-400 font-semibold rounded py-2 px-4 ml-1 mr-3 shadow transition-colors duration-300">
                                            {getButtonText(selectedProviderType.actions.find((item: any) => item.schema && item.schema.is_executable === true)?.value || '', t)}
                                        </button>
                                        <Tooltip description={t("Connections can be tested after the setup workflow is complete.")} position="right" />
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col w-full mr-3">
                                {/* schema form */}
                                {selectedProviderType.actions.filter((item: any) => item.type !== "push").map((item: any) => {
                                    const uiSchema = item.ui_schema ? { ...item.ui_schema } : {};
                                    const formTitle = item.ui_schema && item.ui_schema['ui:title'] ? item.ui_schema['ui:title'] : item.schema.title ? item.schema.title : item.name;
                                    return (
                                        <div
                                            key={item.id}
                                            tabIndex={-1}
                                            onFocus={handleFormInteraction}
                                            className={checkSchema(item) ? "mb-[16px] rounded p-[16px]" : "hidden"}
                                        >
                                            {checkSchema(item) && (
                                                <>
                                                    <div className="mb-[16px] flex flex-col items-start">
                                                        <div className="text-2xl font-normal text-secondary-gray dark:text-neutral-gray-300 transition-all duration-300">{formTitle}</div>
                                                        {uiSchema['ui:description'] && (
                                                            <div className="form-root-description !mb-0 text-gray-600 dark:text-gray-300 mt-1 transition-colors duration-300">
                                                                {uiSchema['ui:description']}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="rounded border border-field-outline dark:border-neutral-gray-600 bg-white dark:bg-dark-gray p-[16px] transition-colors duration-300">
                                                        <SchemaForm
                                                            userIsAdmin={currentMemberIsAdmin}
                                                            formTitle={''}
                                                            schema={item.schema}
                                                            ui_schema={{...uiSchema, 'ui:description': ''}}
                                                            onChange={(e: any) => onChangeSchemaForm(e, item.id)}
                                                            isEditable={true}
                                                            errors={rsjfErrors[item.id]}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                {/* advanced configurations */}
                <div>
                    <div className="mt-6 text-lg font-semibold text-secondary-gray dark:text-gray-lines cursor-pointer" onClick={() => setShowAdvanced(!showAdvanced)}>
                        {showAdvanced ? <ArrowDown /> : <ArrowRight />} {t("Advanced Configurations")}
                    </div>
                    {showAdvanced &&
                        <div className="ml-8 flex flex-col pb-14">
                            <label
                                className="inline-block hover:cursor-pointer text-sm text-secondary-gray dark:text-gray-lines"
                            >
                                {t("Optional Endpoint (For Advanced Users)")}
                            </label>
                            <input
                                id="endpoint"
                                placeholder=""
                                value={initialConnection.base_url}
                                className={inputClasses.standard + " mb-3"}
                                onChange={(e) => onChangeEndpoint(e)}
                                disabled={false}
                            >
                            </input>
                            {errors.base_url && <div className="text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900 dark:bg-opacity-30 py-1 px-2 rounded transition-colors duration-300">{errors.base_url}</div>}
                        </div>}
                </div>
            </div>
        </div>
    );
};

export default NewConnectionStepTwo;